import { createContext, useContext, type RefObject } from 'react';
import type { DepartmentOption } from '../../../people/types';
import type { RoleForm, RoleFormKey } from '../../roleForm';
import type { JobRoleCapabilities, JobRoleContent, JobRoleTemplateDetail, JobRoleTemplateListItem, OccupationItem, OccupationSystem } from '../../types';
import type { GroupedComboboxHandle } from '../guided/GroupedCombobox';

/** What every step of the wizard can read and do. One object, provided by RoleWizardDialog, so the six steps stay small. */
export interface WizardApi {
  uid: string;
  form: RoleForm;
  /** Sets one field and clears its validation message. */
  set: (key: RoleFormKey, value: string) => void;
  setMany: (patch: Partial<RoleForm>) => void;
  errorOf: (key: string) => string | undefined;
  /** True while a save is running. */
  disabled: boolean;
  /** The server's capability flags, or null when unknown (then everything is offered and the server's refusal is handled). */
  caps: JobRoleCapabilities | null;
  editing: boolean;
  /** The role id once it exists (editing, or saved earlier in this dialog). */
  roleId: string | null;

  // ---- Step 1 ----
  startMode: 'template' | 'blank';
  templates: JobRoleTemplateListItem[] | null;
  templatesError: string | null;
  /** The plan does not include the EPN template library (the server refused the list with SUBSCRIPTION_UPGRADE_REQUIRED). */
  templatesLocked: boolean;
  templateId: string | null;
  /** The template chosen on Step 1 whose content has NOT been copied yet (that happens on Next). */
  pendingTemplate: JobRoleTemplateDetail | null;
  templateBusy: boolean;
  templateError: string | null;
  switchStartMode: (mode: 'template' | 'blank') => void;
  chooseTemplate: (t: JobRoleTemplateListItem) => void;
  clearTemplate: () => void;

  // ---- suggestions: the template the role came from (Step 1 or, when editing, the role's source template) ----
  suggestionTemplate: { id: string; title: string; content: JobRoleContent } | null;

  // ---- departments ----
  departments: DepartmentOption[];
  newDepartmentIds: string[];
  requestAddDepartment: (name: string) => void;
  departmentRef: RefObject<GroupedComboboxHandle | null>;

  // ---- occupations ----
  occupation: Partial<Record<OccupationSystem, OccupationItem | null>>;
  setOccupation: (system: OccupationSystem, item: OccupationItem | null) => void;
}

export const WizardContext = createContext<WizardApi | null>(null);

export function useWizard(): WizardApi {
  const api = useContext(WizardContext);
  if (!api) throw new Error('useWizard must be used inside RoleWizardDialog.');
  return api;
}
